/**
 * Feature "goToPage" para DataTables 2.
 *
 * Acrescenta à barra de paginação uma caixa onde se escreve o número da página
 * a que se quer saltar ("Ir para [ 7 ] / 132"), para não ser preciso clicar de
 * página em página em listagens grandes. Funciona tanto em client-side como em
 * server-side (o salto passa pelo `draw('page')`, que refaz o pedido à API sem
 * perder filtros nem ordenação).
 *
 * A feature é registada uma única vez no core; cada tabela ativa-a no `layout`:
 *
 *   layout: { bottomEnd: ["paging", "goToPage"] }
 *
 * Nas tabelas que ainda usam o `dom` antigo (DataTables 1) o `layout` é
 * ignorado, por isso a feature também tem letra própria — "G" — para se poder
 * escrever na string do `dom`, a seguir ao "p" da paginação.
 */
export default function registerGoToPageFeature(DataTablesCore) {
  if (!DataTablesCore || !DataTablesCore.feature) {
    return;
  }

  // Vários componentes importam o core; só o primeiro é que regista.
  if (DataTablesCore.ext.features.goToPage) {
    return;
  }

  DataTablesCore.feature.register("goToPage", function (settings) {
    // Sem paginação não há para onde saltar (ex.: tabelas com paging: false)
    if (!settings.oFeatures.bPaginate) {
      return null;
    }

    const api = new DataTablesCore.Api(settings);

    const host = document.createElement("div");
    host.className = "dt-goto";

    const label = document.createElement("label");
    label.className = "dt-goto-label";
    label.textContent = "Ir para";

    const input = document.createElement("input");
    input.type = "number";
    input.min = "1";
    input.step = "1";
    input.className = "dt-goto-input dt-input";
    input.setAttribute("aria-label", "Ir para a página");

    const total = document.createElement("span");
    total.className = "dt-goto-total";

    label.appendChild(input);
    host.appendChild(label);
    host.appendChild(total);

    // Repõe o número da página atual e o total de páginas. Chamado a cada draw
    // para acompanhar saltos feitos pelos botões, filtros ou pesquisa.
    const update = function () {
      const info = api.page.info();

      // Uma única página: a caixa não serve para nada, fica escondida.
      host.style.display = info.pages > 1 ? "" : "none";

      input.max = String(info.pages);
      input.value = info.pages > 0 ? String(info.page + 1) : "";
      total.textContent = "/ " + info.pages;
    };

    const goToPage = function () {
      const info = api.page.info();
      const escrito = parseInt(input.value, 10);

      // Valor vazio ou inválido: volta a mostrar a página atual
      if (isNaN(escrito) || info.pages === 0) {
        update();
        return;
      }

      // Fora do intervalo salta para o limite mais próximo, em vez de ignorar
      const destino = Math.min(Math.max(escrito, 1), info.pages) - 1;

      if (destino === info.page) {
        update();
        return;
      }

      api.page(destino).draw("page");
    };

    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        // Evita submeter um eventual formulário à volta da tabela
        e.preventDefault();
        goToPage();
        return;
      }

      // Setas do teclado navegam diretamente, como nos botões de paginação
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        const info = api.page.info();
        const destino = info.page + (e.key === "ArrowUp" ? 1 : -1);

        if (destino >= 0 && destino < info.pages) {
          api.page(destino).draw("page");
        }
      }
    });

    // Cobre o clique nas setinhas do input e a saída do campo
    input.addEventListener("change", goToPage);
    input.addEventListener("blur", goToPage);

    // O clique dentro da caixa não deve ser tratado como clique na tabela
    host.addEventListener("click", function (e) {
      e.stopPropagation();
    });

    settings.aoDrawCallback.push(update);

    return host;
  }, "G");
}
